/**
 * The chrome's account and project list, top bar, rail, command palette,
 * tab title, read through here instead of straight off either store.
 *
 * VITE_API_MODE never changes within a page load (the same idiom as
 * AppShell's USE_MOCK / real-api.ts's useMock), so branching which store to
 * read is stable across every render of a given component: mock mode keeps
 * the whole demo apparatus (scenarios, the clock, every ?state= deep link)
 * reading useDemoStore exactly as before; real mode reads the session store
 * AppShell's auth guard populates via getAccount()/listProjects().
 *
 * Pre-auth screens (SignIn, Waitlist) are NOT consumers of this file: they
 * render before a session exists and read the demo store directly even in
 * real mode, since LoopSignIn never touches an account object at all.
 */
import type { Account, Project } from '../types';
import { useDemoStore } from '../mock/store';
import { useSessionStore } from './session';

const USE_MOCK = import.meta.env.VITE_API_MODE === 'mock';

export function useAccount(): Account {
  // eslint-disable-next-line react-hooks/rules-of-hooks -- USE_MOCK is fixed for the app's lifetime
  if (USE_MOCK) return useDemoStore((s) => s.account);
  // Real mode: every consumer of this hook renders inside AppShell, which
  // does not mount <Outlet /> until status is 'signed-in', account is
  // always set by then.
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useSessionStore((s) => s.account) as Account;
}

export function useProjects(): Project[] {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  if (USE_MOCK) return useDemoStore((s) => s.projects);
  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useSessionStore((s) => s.projects);
}
